import React, { useState } from 'react';
import { loginWithGoogle } from '../../firebase';
import { Loader2, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface GoogleLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  variant?: 'navbar' | 'card' | 'inline' | 'hero';
  label?: string;
  hindiLabel?: string;
  className?: string;
}

export default function GoogleLoginButton({
  onSuccess,
  onError,
  variant = 'navbar',
  label = 'Sign in with Google',
  hindiLabel = 'गूगल से लॉगिन करें',
  className = ''
}: GoogleLoginButtonProps) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successState, setSuccessState] = useState(false);

  const handleLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const user = await loginWithGoogle();
      if (user) {
        setSuccessState(true);
        setTimeout(() => {
          onSuccess?.();
        }, 300);
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        // User closed popup deliberately - no scary error needed
        console.warn('Google Auth popup closed by user');
      } else if (code === 'auth/popup-blocked') {
        const msg = 'पॉपअप ब्लॉक हो गया है। कृपया ब्राउज़र में पॉपअप अनुमति दें। / Popup was blocked by your browser. Please allow popups.';
        setErrorMsg(msg);
        onError?.(msg);
      } else {
        const msg = err?.message || 'Google Auth failed. Please try again.';
        setErrorMsg(msg);
        onError?.(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Google 4-Color SVG Icon
  const GoogleIcon = ({ size = 18 }: { size?: number }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0 transition-transform duration-300 group-hover:scale-110"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );

  if (variant === 'card' || variant === 'hero') {
    return (
      <div className="w-full space-y-2">
        <button
          type="button"
          onClick={handleLogin}
          disabled={loading || successState}
          className={`group relative w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-50 text-slate-800 font-bold py-3.5 px-6 rounded-2xl border border-slate-200/90 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] disabled:opacity-70 cursor-pointer overflow-hidden ${className}`}
        >
          {/* Subtle gradient backdrop highlight */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/40 via-emerald-50/20 to-amber-50/30 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

          {loading ? (
            <div className="flex items-center gap-2.5 text-blue-600 font-semibold text-sm">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <span>कनेकशन हो रहा है... / Signing in...</span>
            </div>
          ) : successState ? (
            <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
              <CheckCircle2 className="w-5 h-5 animate-bounce" />
              <span>सफल! / Logged In!</span>
            </div>
          ) : (
            <>
              <GoogleIcon size={20} />
              <div className="flex flex-col text-left">
                <span className="text-xs sm:text-sm font-extrabold text-slate-800 tracking-tight leading-tight group-hover:text-blue-600 transition-colors">
                  {label}
                </span>
                <span className="text-[10px] text-slate-500 font-medium leading-none mt-0.5">
                  {hindiLabel}
                </span>
              </div>
              <Sparkles className="w-4 h-4 text-blue-500 ml-auto opacity-0 group-hover:opacity-100 transition-all transform group-hover:rotate-12" />
            </>
          )}
        </button>

        <AnimatePresence>
          {errorMsg && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-semibold flex items-center gap-2 text-left"
            >
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span>{errorMsg}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Standard / Navbar Variant
  return (
    <button
      type="button"
      onClick={handleLogin}
      disabled={loading || successState}
      className={`group relative inline-flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-slate-50/90 border border-slate-200 hover:border-blue-400/80 rounded-xl transition-all text-slate-700 hover:text-blue-600 cursor-pointer active:scale-95 text-xs font-extrabold shrink-0 shadow-xs hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      title="Login with Google Account / गूगल से लॉगिन करें"
      aria-label="Login with Google"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
      ) : successState ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
      ) : (
        <GoogleIcon size={16} />
      )}
      <span className="hidden sm:inline whitespace-nowrap">
        {loading ? 'Connecting...' : 'Google Sign In'}
      </span>
      <span className="inline sm:hidden whitespace-nowrap">
        {loading ? '...' : 'Login'}
      </span>
    </button>
  );
}
